import { NavLink } from "react-router-dom";
import {
  ShoppingCart,
  ClipboardList,
  ChefHat,
  Package,
  Users,
  Clock,
  BarChart3,
  Calendar,
  UserCog,
  Shield,
  Coffee,
  Settings,
  Settings2,
  FolderTree,
  LogOut,
  X,
  Tag,
  RotateCcw,
  Database,
  CloudOff,
  Cloud,
  CloudUpload,
} from "lucide-react";
import { cn, SHOP_NAME } from "@/lib/utils";
import { useAuthStore, usePermissions } from "@/stores";
import { useSettings } from "@/features/settings/api";
import { useOnlineStatus, useOfflineQueue } from "@/hooks";
import { Badge, Button, ScrollArea, Separator } from "@/components/ui";
import { HappyHourBanner } from "./HappyHourBanner";

interface NavItem {
  path: string;
  label: string;
  icon: React.ElementType;
  permission?: string | null;
}

const mainNavItems: NavItem[] = [
  { path: "/pos", label: "POS", icon: ShoppingCart },
  { path: "/orders", label: "Orders", icon: ClipboardList },
  {
    path: "/refunds",
    label: "Refunds",
    icon: RotateCcw,
    permission: "refunds.read",
  },
  { path: "/kitchen", label: "Kitchen", icon: ChefHat },
  {
    path: "/inventory",
    label: "Inventory",
    icon: Package,
    permission: "inventory.read",
  },
  {
    path: "/customers",
    label: "Customers",
    icon: Users,
    permission: "customers.read",
  },
  { path: "/shifts", label: "Shifts", icon: Clock, permission: "shifts.read" },
];

const reportsNavItems: NavItem[] = [
  {
    path: "/reports",
    label: "Dashboard",
    icon: BarChart3,
    permission: "reports.read",
  },
  {
    path: "/end-of-day",
    label: "End of Day",
    icon: Calendar,
    permission: "reports.read",
  },
];

const adminNavItems: NavItem[] = [
  { path: "/users", label: "Users", icon: UserCog, permission: "users.read" },
  { path: "/roles", label: "Roles", icon: Shield, permission: "roles.read" },
  {
    path: "/products",
    label: "Products",
    icon: Coffee,
    permission: "products.read",
  },
  {
    path: "/categories",
    label: "Categories",
    icon: FolderTree,
    permission: "products.read",
  },
  {
    path: "/attributes",
    label: "Attributes",
    icon: Settings2,
    permission: "products.read",
  },
  {
    path: "/promotions",
    label: "Promotions",
    icon: Tag,
    permission: "settings.read",
  },
  {
    path: "/settings",
    label: "Settings",
    icon: Settings,
    permission: "settings.read",
  },
  {
    path: "/backup",
    label: "Backup & Restore",
    icon: Database,
    permission: "data.export",
  },
];

interface SidebarSectionProps {
  title?: string;
  items: NavItem[];
  onNavigate?: () => void;
}

function SidebarSection({ title, items, onNavigate }: SidebarSectionProps) {
  const { hasPermission } = usePermissions();
  const visible = items.filter(
    (item) => !item.permission || hasPermission(item.permission),
  );

  if (visible.length === 0) {
    return null;
  }

  return (
    <div className="space-y-1">
      {title && (
        <p className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-sidebar-foreground/40">
          {title}
        </p>
      )}

      {visible.map((item) => (
        <NavLink
          key={item.path}
          to={item.path}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
              isActive
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground",
            )
          }
        >
          <item.icon className="h-[18px] w-[18px] shrink-0" />
          <span>{item.label}</span>
        </NavLink>
      ))}
    </div>
  );
}

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

export function Sidebar({ open, onClose }: SidebarProps) {
  const { user, logout } = useAuthStore();
  const { hasPermission } = usePermissions();
  const { data: settings } = useSettings();
  const online = useOnlineStatus();
  const { pendingCount, failedCount } = useOfflineQueue();

  const shopName = settings?.shop_name ?? SHOP_NAME;
  const userName = user
    ? `${user.firstName} ${user.lastName}`.trim() || user.username
    : "User";
  const roleName = user?.role?.name ?? "User";

  const sectionVisible = (items: NavItem[]) =>
    items.some((i) => !i.permission || hasPermission(i.permission));

  const showReports = sectionVisible(reportsNavItems);
  const showAdmin = sectionVisible(adminNavItems);

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={cn(
          "fixed left-0 top-0 z-50 flex h-screen w-64 flex-col bg-sidebar text-sidebar-foreground transition-transform duration-200 md:z-40 md:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="flex h-16 items-center justify-between px-5">
          <h1 className="text-lg font-bold tracking-tight">{shopName}</h1>
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden text-sidebar-foreground hover:bg-sidebar-accent"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
        {(!online || pendingCount > 0) && (
          <NavLink
            to="/orders/pending"
            onClick={onClose}
            className={cn(
              "mx-3 mb-2 flex items-center justify-between rounded-md px-3 py-2 text-xs font-medium transition-colors",
              !online
                ? "bg-amber-500/15 text-amber-100 hover:bg-amber-500/25"
                : "bg-sidebar-accent text-sidebar-foreground hover:bg-sidebar-accent/80",
            )}
          >
            <span className="flex items-center gap-2">
              {!online ? (
                <CloudOff className="h-4 w-4" />
              ) : (
                <CloudUpload className="h-4 w-4" />
              )}
              {!online ? "Offline" : "Syncing"}
            </span>
            {pendingCount > 0 && (
              <Badge
                variant={failedCount > 0 ? "destructive" : "secondary"}
                className="h-5 px-1.5 text-[10px]"
              >
                {pendingCount}
              </Badge>
            )}
          </NavLink>
        )}

        {online && pendingCount === 0 && (
          <div className="mx-3 mb-2 flex items-center gap-2 px-3 py-1 text-[11px] text-sidebar-foreground/50">
            <Cloud className="h-3.5 w-3.5" />
            All synced
          </div>
        )}

        <HappyHourBanner />
        <ScrollArea className="flex-1 px-3 py-4">
          <nav className="space-y-4">
            <SidebarSection items={mainNavItems} onNavigate={onClose} />
            {showReports && (
              <>
                <Separator className="bg-sidebar-border" />
                <SidebarSection
                  title="Reports"
                  items={reportsNavItems}
                  onNavigate={onClose}
                />
              </>
            )}

            {showAdmin && (
              <>
                <Separator className="bg-sidebar-border" />
                <SidebarSection
                  title="Admin"
                  items={adminNavItems}
                  onNavigate={onClose}
                />
              </>
            )}
          </nav>
        </ScrollArea>
        <Separator className="bg-sidebar-border" />
        <div className="p-4">
          <div className="mb-3">
            <p className="truncate text-sm font-medium">{userName}</p>
            <p className="truncate text-xs capitalize text-sidebar-foreground/60">
              {roleName}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={logout}
            className="w-full justify-center gap-2 border-sidebar-border bg-transparent text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground"
          >
            <LogOut className="h-4 w-4" />
            Logout
          </Button>
        </div>
      </aside>
    </>
  );
}
